/**
 * WAYP-927 — Resolve the user-customisable name for the "Driver Route" module.
 *
 * The module name lives on the `mod_usps_main` enterprise feature under the
 * `bids_module_name` preference key (set via the Enterprise Features admin
 * page → USPS Carrier → "Module Name"). Customers commonly retitle the
 * module — V1 ships "BID" / "Driver Route" / "Schedule" depending on the
 * deployment.
 *
 * Returns both singular and plural forms so list pages, modals, toasts,
 * sidebar labels, and CSV exports can all stay in sync. Pluralisation is
 * a simple "+s" — sufficient for the values customers actually use ("BID",
 * "Driver Route", "Schedule"). When the stored value is missing, empty,
 * or still on the legacy "USPS Carrier" default, we fall back to
 * "Driver Route" / "Driver Routes" so existing screens never go blank.
 */
import { useFeaturePreference } from './use-enterprise-features';

export interface ModuleNameLabel {
  singular: string;
  plural:   string;
}

const DEFAULT_SINGULAR = 'Driver Route';
const DEFAULT_PLURAL   = 'Driver Routes';

// "USPS Carrier" was the historical schema default for `bids_module_name`
// but it's the wrong label for the Driver Route module — treat it as a
// not-set value and fall through to the friendly default.
const LEGACY_PLACEHOLDERS = new Set<string>(['', 'USPS Carrier']);

export function useDriverRouteModuleName(): ModuleNameLabel {
  const raw = useFeaturePreference<string>(
    'mod_usps_main',
    'bids_module_name',
    DEFAULT_SINGULAR,
  );
  const cleaned = (raw || '').trim();

  if (!cleaned || LEGACY_PLACEHOLDERS.has(cleaned)) {
    return { singular: DEFAULT_SINGULAR, plural: DEFAULT_PLURAL };
  }

  // Naive but adequate pluralisation: append "s" unless the user already
  // typed a plural-looking value. Module names are short labels (BID,
  // Schedule, Driver Route) — no need for inflection lib heroics.
  const plural = cleaned.toLowerCase().endsWith('s') ? cleaned : `${cleaned}s`;
  return { singular: cleaned, plural };
}
